import { decodeUnknownSync, encodeSync } from '@effect/schema/Schema';
import { ChecksumAddress, makeAddressSync, ProtocolInstance } from '@silo/types';
import { durationToMilliseconds } from '@silo/utils';
import { useSiloWeb3 } from '@silo/web3';
import { MutationFunction, QueryFunction, useQuery } from '@tanstack/react-query';
import { GetHasAgreedBody, PostAgreementBody, TermsOfUseAction } from 'data/terms-of-use/types';
import { isNullable } from 'effect/Predicate';

export const termsOfUseAgreementKeyName = 'terms-of-use-agreement';

export type TermsOfUseAgreementKey = [key: typeof termsOfUseAgreementKeyName, user: ChecksumAddress];

const staleTime = durationToMilliseconds({ minutes: 10 });

export const useQueryTermsOfUseAgreement = () => {
  const { account } = useSiloWeb3();

  const { data } = useQuery({
    queryKey: [termsOfUseAgreementKeyName, account!],
    queryFn: queryHasAgreed,
    enabled: !!account,
    staleTime,
  });

  const hasAgreed = getHasAgreed(data);

  return {
    hasAgreed,
  };
};

function getHasAgreed(data: boolean | undefined): boolean {
  if (isNullable(data)) return true;

  return data;
}

const queryHasAgreed: QueryFunction<boolean, TermsOfUseAgreementKey> = async ({ queryKey: [, user] }) => {
  try {
    const response = await fetch(`/api/terms-of-use/${user}`);

    const { hasAgreed } = await response.json().then(decodeUnknownSync(GetHasAgreedBody));

    return hasAgreed;
  } catch (error) {
    console.warn(error);

    /**
     * In the case of an error, assume the user has agreed - do not want to
     * block user transactions in this case.
     */
    return true;
  }
};

interface PostAgreementVariables {
  user: ChecksumAddress;
  protocolInstance: ProtocolInstance;
  market: ChecksumAddress;
  action: TermsOfUseAction;
}

export const postAgreement: MutationFunction<void, PostAgreementVariables> = async ({
  user,
  protocolInstance,
  market: checksumMarket,
  action,
}) => {
  const market = makeAddressSync(checksumMarket);

  const body = encodeSync(PostAgreementBody)({ protocolInstance, market, action });

  await fetch(`/api/terms-of-use/${user}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
};
